import { Link } from '@tanstack/react-router'
import { formatNumber } from '../../shared/ui/charts/scale'
import { CHECK_LABELS, formatDateTime, shortId } from './labels'
import { useAuditFindings, useAuditRuns, useRunAudit, useSetFindingStatus } from './queries'
import styles from './lunars.module.css'

const STATUS_LABELS: Readonly<Record<string, string>> = { open: 'Новое', seen: 'Просмотрено', resolved: 'Исправлено' }

function detailsText(details: Readonly<Record<string, unknown>>): string {
  return Object.entries(details)
    .filter(([k]) => k !== 'user_id')
    .map(([k, v]) => `${k}: ${String(v)}`)
    .join(', ')
}

function runLabel(status: string, count: number): string {
  if (status !== 'ok') return status
  return count > 0 ? `находок ${formatNumber(count)}` : 'чисто'
}

export function ChecksTab() {
  const runs = useAuditRuns()
  const findings = useAuditFindings()
  const run = useRunAudit()
  const mark = useSetFindingStatus()
  const last = runs.data?.items[0]

  return (
    <div className={styles.stack}>
      <div className="card-panel">
        <div className={styles.head} style={{ marginBottom: 0 }}>
          <div>
            <h2 style={{ margin: 0 }}>Сверка происхождения лунаров</h2>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Каждую ночь в 03:00 по Москве. Последний запуск: {last ? formatDateTime(last.started_at) : 'ещё не было'}
            </p>
          </div>
          <button onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? 'Идёт сверка…' : 'Запустить сейчас'}
          </button>
        </div>
        {run.isError && <p className="error-box">Не удалось запустить сверку.</p>}
      </div>
      <div className={styles.split}>
        <div className="card-panel">
          <p className={styles.caption}>Запуски</p>
          {runs.data?.items.map((r) => (
            <div key={r.id} className={styles.line}>
              <span>
                {formatDateTime(r.started_at)} · {r.trigger === 'cron' ? 'по расписанию' : 'вручную'}
              </span>
              <span className={r.findings_count > 0 ? 'badge danger' : 'badge ok'}>{runLabel(r.status, r.findings_count)}</span>
            </div>
          ))}
          {runs.data?.items.length === 0 && <p className="muted">Запусков ещё не было.</p>}
        </div>
        <div className="card-panel">
          <p className={styles.caption}>Находки</p>
          <div className={styles.scroll}>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Проверка</th>
                  <th>Объект</th>
                  <th>Детали</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {findings.data?.items.map((f) => (
                  <tr key={f.id}>
                    <td>{CHECK_LABELS[f.check_code] ?? f.check_code}</td>
                    <td className={styles.mono}>
                      {f.object_type === 'user' ? (
                        <Link to="/users/$userId" params={{ userId: f.object_id.split(':')[0] ?? f.object_id }}>
                          {shortId(f.object_id)}
                        </Link>
                      ) : (
                        shortId(f.object_id)
                      )}
                    </td>
                    <td className="muted">{detailsText(f.details)}</td>
                    <td>
                      <span className={f.status === 'open' ? 'badge danger' : 'badge'}>{STATUS_LABELS[f.status] ?? f.status}</span>
                    </td>
                    <td>
                      {f.status !== 'resolved' && (
                        <button
                          className="secondary"
                          disabled={mark.isPending}
                          onClick={() => mark.mutate({ id: f.id, status: f.status === 'open' ? 'seen' : 'open' })}
                        >
                          {f.status === 'open' ? 'Просмотрено' : 'Вернуть'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {findings.data?.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Находок нет.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
